import { ROLES, type RoleId } from '@onuw/roles-data'
import { useTranslation } from 'react-i18next'
import { cardArt, cardBackFrame, cardFrame, frameImage, type ResponsiveImage } from '../lib/assets'
import styles from './Card.module.css'
import { useZoomTrigger } from './zoomStore'

export interface CardProps {
  role?: RoleId | undefined
  /** Face-up shows the role; face-down shows the card back. */
  faceUp?: boolean
  /** CSS width (any unit). The layout and the text scale with the card. */
  width?: string
  /** Rendered greyed out (not selectable). */
  disabled?: boolean
  /** `sizes` attribute for the responsive images. */
  sizes?: string
  className?: string
  /**
   * A tap (touch, unless the card is inside a button or link), a long-press, a right-click and (small cards) a
   * hover open the enlarged card. On by default.
   */
  zoomable?: boolean
  /** The hover preview alone; off where the whole card is on screen anyway (the lobby's role picker). */
  hoverZoom?: boolean | undefined
  /** The role's place in the night's wake order, shown small on the art (the roles page). */
  wakeOrder?: number | undefined
}

/** Joins class names, dropping empty/undefined parts. */
export const cx = (...parts: (string | undefined | false | null)[]) => parts.filter(Boolean).join(' ')

function Picture({
  image,
  sizes,
  className,
}: {
  image: ResponsiveImage
  sizes: string
  className: string | undefined
}) {
  if (!image.available) return <div className={className} aria-hidden />
  return (
    <picture className={className}>
      <source type="image/avif" srcSet={image.avifSet} sizes={sizes} />
      <source type="image/webp" srcSet={image.webpSet} sizes={sizes} />
      <img src={image.src} alt="" sizes={sizes} loading="lazy" decoding="async" draggable={false} />
    </picture>
  )
}

/** s/m/l buckets that pick the type size (and, for ability text, the width below which it moves off the card). */
const lengthClass = (text: string, short: number, long: number) =>
  text.length <= short ? 's' : text.length <= long ? 'm' : 'l'

/**
 * A role card: art, team frame (vector, drawn for the oval shape) and real text. Wide cards show the name in
 * the nameplate and the ability on the plaque; small cards switch to a compact face (art + name) and open the
 * full card on a tap / long-press / hover. See Card.module.css for the size rules.
 */
export function Card({
  role,
  faceUp = true,
  width = '160px',
  disabled,
  sizes = '160px',
  className,
  zoomable = true,
  hoverZoom = true,
  wakeOrder,
}: CardProps) {
  const { t } = useTranslation()
  const def = role ? ROLES[role] : undefined
  const showFace = Boolean(faceUp && role && def)
  const title = role ? t(`roles:${role}.title`) : ''
  const description = role ? t(`roles:${role}.description`) : ''
  const zoom = useZoomTrigger(showFace && zoomable ? role : undefined, { hover: hoverZoom })
  return (
    <div
      ref={zoom.ref}
      className={cx(
        styles.card,
        showFace ? styles.faceUp : styles.faceDown,
        disabled && styles.disabled,
        className,
      )}
      style={{ width }}
      data-card
      data-team={showFace ? def?.team : undefined}
      data-name={showFace ? lengthClass(title, 10, 15) : undefined}
      data-len={showFace ? lengthClass(description, 55, 90) : undefined}
      role="img"
      aria-label={showFace ? `${title}: ${description}` : t('ui:generic.cardBack', 'Face-down card')}
      {...zoom.handlers}
    >
      {showFace && role && def ? (
        <>
          <Picture image={cardArt(role)} sizes={sizes} className={styles.art} />
          <img
            className={cx(styles.frame, styles.frameFull)}
            src={cardFrame(def.team)}
            alt=""
            draggable={false}
          />
          <img
            className={cx(styles.frame, styles.frameMini)}
            src={cardFrame(def.team, 'mini')}
            alt=""
            draggable={false}
          />
          <div className={styles.title} aria-hidden>
            {title}
          </div>
          <div className={styles.description} aria-hidden>
            {description}
          </div>
          {wakeOrder !== undefined && (
            <div className={styles.order} aria-hidden>
              {wakeOrder}
            </div>
          )}
        </>
      ) : (
        <>
          <Picture image={frameImage('back')} sizes={sizes} className={styles.back} />
          <img className={styles.frame} src={cardBackFrame()} alt="" draggable={false} />
        </>
      )}
    </div>
  )
}
